import { IconButton, Menu, MenuItem } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LocationType } from '../types';
import { Start } from '@mui/icons-material';
import { LocationAssociationEntityType } from '@kinetic-ui/data-layer';
import { useAssociatedEntities } from 'kineticUtils/locations/useAssociatedEntities';
import { useRouteBuilder } from 'kineticUtils/hooks/useRouteBuilder';
import { getUrlWithQueryParams } from 'kineticUtils/routing/getUrlWithQueryParams';
import { LOCATION_ID } from 'const/queryParams';

const SignalMenuItem = ({
  locationType,
  locationId,
}: {
  locationType: LocationType | undefined;
  locationId: string;
}) => {
  const { associatedEntities } = useAssociatedEntities();

  const associatedSignal = associatedEntities?.find(
    (entity) => entity.entityType === LocationAssociationEntityType.SignalsIntersection
  );

  const { t } = useTranslation('locations');
  const signalRoute = useRouteBuilder('signals/intersections');
  const addSignalRoute = getUrlWithQueryParams(signalRoute('add/details'), {
    [LOCATION_ID]: locationId,
  });
  const editSignalRoute = useRouteBuilder(signalRoute('edit'));
  const disabled = locationType !== 'Intersection';

  if (!associatedSignal) {
    return (
      <MenuItem component={Link} to={addSignalRoute} disabled={disabled}>
        {t('createSignal')}
      </MenuItem>
    );
  }
  return (
    <MenuItem component={Link} to={editSignalRoute(associatedSignal.entityId)} disabled={disabled}>
      {t('editSignal')}
    </MenuItem>
  );
};

const RampMeterMenuItem = ({
  locationType,
  locationId,
}: {
  locationType: LocationType | undefined;
  locationId: string;
}) => {
  const { associatedEntities } = useAssociatedEntities();

  const associatedRamp = associatedEntities?.find(
    (entity) => entity.entityType === LocationAssociationEntityType.RampMeterSite
  );

  const { t } = useTranslation('locations');
  const rampRoute = useRouteBuilder('ramp-meter');
  const addRampMeterRoute = getUrlWithQueryParams(rampRoute('create'), {
    [LOCATION_ID]: locationId,
  });
  const editRampRoute = useRouteBuilder(rampRoute('edit'));
  const disabled = locationType !== 'Ramp';

  if (!associatedRamp) {
    return (
      <MenuItem component={Link} to={addRampMeterRoute} disabled={disabled}>
        {t('createRampMeter')}
      </MenuItem>
    );
  }
  return (
    <MenuItem component={Link} to={editRampRoute(associatedRamp.entityId)} disabled={disabled}>
      {t('editRampMeter')}
    </MenuItem>
  );
};

export const WorkflowButton = ({
  locationId,
  locationType,
  disabled,
}: {
  locationId: string;
  locationType?: LocationType;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('locations');
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  return (
    <>
      <Tooltip title={t('continueWorkflow')}>
        <span>
          <IconButton size='small' onClick={handleIconClick} disabled={disabled}>
            <Start fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id='workflow-menu'
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'workflow-button',
        }}
      >
        <SignalMenuItem locationId={locationId} locationType={locationType} />
        <RampMeterMenuItem locationId={locationId} locationType={locationType} />
      </Menu>
    </>
  );
};
